import type { Song } from '../types';

type Props = {
  songs: Song[];
  activeId: string | null;
  onSelect: (id: string) => void;
  onDelete: (id: string) => void;
  /** Toggles a song's `favourite` flag. Surfaces as a star button to
   *  the left of delete-×; favourited songs also pin to the Home
   *  page's Favourites strip. */
  onToggleFavourite: (id: string) => void;
};

const ROW_BASE =
  'song-item group flex items-center p-2.5 rounded cursor-pointer mb-0.5 gap-2 hover:bg-zinc-800';
const ROW_ACTIVE = 'bg-zinc-800 border-l-[3px] border-yellow-300 pl-[7px]';
const TITLE_BASE =
  'flex-1 text-zinc-100 text-xs overflow-hidden text-ellipsis whitespace-nowrap';
const TITLE_ACTIVE = 'text-yellow-300 font-semibold';
// Star button: shows when starred OR on row hover (so users can
// discover the affordance). Filled star uses the brand yellow.
const FAV_BASE =
  'bg-transparent border-0 text-base leading-none px-1.5 py-0.5 rounded transition-colors cursor-pointer';
const FAV_INACTIVE =
  FAV_BASE + ' text-zinc-600 opacity-0 group-hover:opacity-100 hover:text-yellow-300';
const FAV_ACTIVE = FAV_BASE + ' text-yellow-300 opacity-100 hover:text-yellow-200';
const DEL_CLS =
  'bg-transparent border-0 text-zinc-500 text-base leading-none px-1.5 py-0.5 rounded opacity-0 group-hover:opacity-100 hover:bg-red-400 hover:text-white cursor-pointer';

export function SongList({
  songs,
  activeId,
  onSelect,
  onDelete,
  onToggleFavourite,
}: Props) {
  if (songs.length === 0) {
    return <div className="text-zinc-500 p-4 text-xs">No songs yet. Hit + New.</div>;
  }

  return (
    <>
      {songs.map((song) => {
        const isActive = song.id === activeId;
        const isFav = !!song.favourite;
        const label = song.title || '(untitled)';
        return (
          <div
            key={song.id}
            role="button"
            tabIndex={0}
            aria-label={'Load song ' + label}
            className={ROW_BASE + (isActive ? ' ' + ROW_ACTIVE : '')}
            onClick={() => onSelect(song.id)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(song.id);
              }
            }}
          >
            <button
              type="button"
              className={isFav ? FAV_ACTIVE : FAV_INACTIVE}
              aria-label={(isFav ? 'Unfavourite ' : 'Favourite ') + label}
              aria-pressed={isFav}
              title={isFav ? 'Remove from favourites' : 'Add to favourites'}
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavourite(song.id);
              }}
            >
              {isFav ? '★' : '☆'}
            </button>
            <span className={TITLE_BASE + (isActive ? ' ' + TITLE_ACTIVE : '')}>
              {label}
            </span>
            <button
              type="button"
              className={DEL_CLS}
              aria-label={'Delete ' + label}
              onClick={(e) => {
                e.stopPropagation();
                onDelete(song.id);
              }}
            >
              ×
            </button>
          </div>
        );
      })}
    </>
  );
}
